import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { ShieldCheck, CheckCircle2, Search, Loader2, AlertTriangle } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { PageHeader } from "@/components/CrudModule";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";

const STATUS_CLS = {
  active: "bg-emerald-50 text-emerald-700 ring-emerald-200",
  pending_verification: "bg-[#B28A3D]/12 text-[#7a5d27] ring-[#B28A3D]/30",
  expired: "bg-amber-50 text-amber-700 ring-amber-200",
  disabled: "bg-slate-100 text-slate-500 ring-slate-200",
};

export default function CredentialMigrationReview() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("");
  const [scope, setScope] = useState("all");
  const [onlyReview, setOnlyReview] = useState(false);

  const load = () => {
    setLoading(true);
    api.get("/credentials").then(({ data }) => setItems(data)).catch(() => {}).finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const verify = async (id) => {
    try { await api.put(`/credentials/${id}`, { status: "active" }); toast.success("Marked verified & active"); load(); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const stats = useMemo(() => {
    const by = {}; let review = 0;
    items.forEach((c) => { by[c.scope_type] = (by[c.scope_type] || 0) + 1; if (c.status === "pending_verification") review++; });
    return { by, review, total: items.length };
  }, [items]);

  const rows = items.filter((c) => {
    if (scope !== "all" && c.scope_type !== scope) return false;
    if (onlyReview && c.status !== "pending_verification") return false;
    const q = filter.toLowerCase();
    return !q || `${c.connects_to} ${c.label} ${c.scope_type} ${c.owner_name}`.toLowerCase().includes(q);
  });

  const scopes = ["all", "carrier", "agency", "lead_vendor", "quote_tool", "crm", "licensing"];

  return (
    <div>
      <PageHeader title="Credential Migration Review" subtitle="Oversight of migrated business credentials. Secrets are never shown here — only metadata, status, and access scope." />

      <div className="flex items-start gap-2 rounded-lg border border-[#315A7D]/20 bg-[#315A7D]/5 px-4 py-2.5 text-[12px] text-[#2c5170] mb-4">
        <ShieldCheck className="h-4 w-4 shrink-0 mt-0.5" />
        <span><strong>{stats.total}</strong> credentials in the encrypted Vault · <strong>{stats.review}</strong> pending verification.
          By type: {Object.entries(stats.by).map(([k, v]) => `${k.replace("_", " ")} ${v}`).join(" · ")}. Recovery codes, OTP/MFA, banking, card data and SSNs were excluded by policy.</span>
      </div>

      <div className="flex items-center gap-2 mb-4 flex-wrap">
        <div className="relative">
          <Search className="absolute left-2.5 top-2.5 h-3.5 w-3.5 text-muted-foreground" />
          <Input placeholder="Search provider, owner, scope…" value={filter} onChange={(e) => setFilter(e.target.value)} className="pl-8 w-64" data-testid="cmr-search" />
        </div>
        {scopes.map((sc) => (
          <button key={sc} onClick={() => setScope(sc)}
            className={`rounded-full px-3 py-1 text-xs font-medium ring-1 ring-inset capitalize transition-colors ${scope === sc ? "bg-[#17324D] text-white ring-[#17324D]" : "bg-card text-muted-foreground ring-border hover:bg-secondary"}`}>
            {sc.replace("_", " ")}
          </button>
        ))}
        <label className="ml-auto flex items-center gap-1.5 text-[12px] text-muted-foreground">
          <input type="checkbox" checked={onlyReview} onChange={(e) => setOnlyReview(e.target.checked)} data-testid="cmr-only-review" />
          Needs review only
        </label>
      </div>

      {loading ? (
        <div className="grid place-items-center py-16 text-muted-foreground"><Loader2 className="h-5 w-5 animate-spin" /></div>
      ) : (
        <div className="rounded-lg border border-border bg-card overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="hover:bg-transparent">
                {["System / Provider", "Type", "Owner", "Role Access", "Status", "Auto-Launch", "Last Verified", "Import Source", "Review", ""].map((h) => (
                  <TableHead key={h} className="overline whitespace-nowrap">{h}</TableHead>
                ))}
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map((c) => (
                <TableRow key={c.id} className="text-[13px]" data-testid={`cmr-${c.id}`}>
                  <TableCell className="font-medium max-w-[220px] truncate" title={c.connects_to || c.label}>{c.connects_to || c.label}</TableCell>
                  <TableCell className="capitalize whitespace-nowrap">{c.scope_type?.replace("_", " ")}</TableCell>
                  <TableCell className="whitespace-nowrap">{c.owner_name}</TableCell>
                  <TableCell className="whitespace-nowrap text-xs text-muted-foreground">{(c.allowed_roles || []).map((r) => r === "ops_manager" ? "ops" : r).join(", ")}</TableCell>
                  <TableCell><span className={`inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-medium ring-1 ring-inset capitalize ${STATUS_CLS[c.status]}`}>{c.status?.replace("_", " ")}</span></TableCell>
                  <TableCell>{c.auto_launch_allowed ? <span className="text-emerald-600 text-xs">On</span> : <span className="text-muted-foreground text-xs">Off</span>}</TableCell>
                  <TableCell className="whitespace-nowrap text-xs text-muted-foreground">{c.last_verified ? new Date(c.last_verified).toLocaleDateString() : "—"}</TableCell>
                  <TableCell className="whitespace-nowrap text-xs text-muted-foreground">{(c.source || "").replace("migration:", "")}</TableCell>
                  <TableCell>{c.status === "pending_verification" ? <AlertTriangle className="h-4 w-4 text-amber-500" /> : <CheckCircle2 className="h-4 w-4 text-emerald-500" />}</TableCell>
                  <TableCell>
                    {c.status !== "active" && (
                      <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => verify(c.id)} data-testid={`verify-${c.id}`}>Mark Verified</Button>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          {rows.length === 0 && <p className="py-10 text-center text-sm text-muted-foreground">No credentials match.</p>}
        </div>
      )}
    </div>
  );
}
